import { useReportExportContext } from "./hooks/useReportExportContext";
import { useReportStateContext } from "./hooks/useReportState";
import { getErrorMessage } from "./types/types";
import {
  buildScorecardSpecification,
  computeScorecard,
  currentPeriod,
  formatScorecardValue,
  monthRange,
  periodFromDateRange,
  periodLabel,
  PLAN_CONFIG_KEY,
  SCORECARD_ROW_KIND,
  shiftPeriod,
  ytdRange,
} from "./utils/scorecardUtils";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import DefaultLoader from "components/common/DefaultLoader";
import * as labels from "config.labels";
import { REPORTS_V3_REVIEW_SCORECARD } from "flags";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as reportsV3Service from "services/reportsV3Service";
import * as staticDataService from "services/staticDataService";
import { useLocalStorageJsonState } from "store/hooks/stateHooks";
import * as staticDataHooks from "store/hooks/staticDataHooks";

const rowSx = (kind) => {
  switch (kind) {
    case SCORECARD_ROW_KIND.SUBTOTAL:
      return { "& td, & th": { fontWeight: 600, backgroundColor: "grey.100" } };
    case SCORECARD_ROW_KIND.TOTAL:
      return {
        "& td, & th": {
          fontWeight: 700,
          borderTop: "2px solid",
          borderTopColor: "divider",
        },
      };
    case SCORECARD_ROW_KIND.YTD:
      return {
        "& td, & th": {
          fontWeight: 700,
          backgroundColor: "grey.100",
          borderBottom: "none",
        },
      };
    default:
      return {};
  }
};

const negativeSx = (value) =>
  typeof value === "number" && value < 0 ? { color: "error.main" } : {};

/**
 * The Review tab's scorecard: configured buckets by measures for one calendar
 * month, with subtotal, total and year-to-date rows and plan figures from the
 * company's `review_scorecard_plan` document.
 *
 * `config` is the resolved `submission_review_config.scorecard` block that
 * buildDashboardReport attaches to the report (validated against the table's
 * real columns); the panel renders nothing without it or without the
 * `reports_v3.review_scorecard` visibility tag.
 *
 * It queries review_list itself (month and YTD), so the grid's other filters do
 * not change its numbers; it does honour the tab's "as at" date. It opens on
 * the month the grid's date filter is set to when that is exactly one calendar
 * month, else the current month.
 *
 * It starts collapsed; whether the user expanded it is remembered in local
 * storage under `expandedStorageKey`, which the host page supplies.
 */
const ReviewScorecard = ({
  config: resolvedConfig,
  displayNameMap,
  expandedStorageKey,
}) => {
  const { isVisible } = staticDataHooks.useIsVisible(
    REPORTS_V3_REVIEW_SCORECARD
  );
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  const { filterMaps } = useReportStateContext();
  const { asAt } = useReportExportContext() ?? {};
  const config = isVisible ? resolvedConfig ?? null : null;
  const { data: plan } = staticDataService.useConfigQuery(PLAN_CONFIG_KEY, {
    skip: !config,
  });
  const [runImmediateReport] = reportsV3Service.useRunImmediateReportMutation();

  // Default the month from the grid's date filter the first time the config is
  // available (the report, and so the config, can arrive after first render).
  const gridRange = config ? filterMaps?.date?.[config.periodColumn] : null;
  const [period, setPeriod] = useState(null);
  useEffect(() => {
    if (!config || period) return;
    setPeriod(periodFromDateRange(gridRange) ?? currentPeriod());
  }, [config, gridRange, period]);

  const [expanded, setExpanded] = useLocalStorageJsonState(expandedStorageKey, {
    default: false,
  });
  const [rows, setRows] = useState(null);
  const [ytdRows, setYtdRows] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const runRange = useCallback(
    async (range) => {
      const specification = buildScorecardSpecification(
        config,
        range,
        displayNameMap ?? {}
      );
      const data = await runImmediateReport({
        report: {
          type: "SINGLE_TABLE_REPORT",
          displayName: "Review scorecard",
          description: "Review scorecard",
          specification,
        },
        outputType: "JSON",
        ...(asAt ? { asAt } : {}),
      }).unwrap();
      return data?.[0]?.tableContents ?? [];
    },
    [config, displayNameMap, runImmediateReport, asAt]
  );

  useEffect(() => {
    if (!config || !period) return undefined;
    let cancelled = false;
    // Drop the previous month's figures so they are never shown under the new
    // month's label while the queries run.
    setRows(null);
    setYtdRows(null);
    setIsLoading(true);
    setError(null);
    const { year, month } = period;
    Promise.all([
      runRange(monthRange(year, month)),
      config.ytd ? runRange(ytdRange(year, month)) : Promise.resolve(null),
    ])
      .then(([monthRows, yearRows]) => {
        if (cancelled) return;
        setRows(monthRows);
        setYtdRows(yearRows);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(getErrorMessage(e) || "Failed to load the scorecard");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [config, period, runRange]);

  const table = useMemo(
    () =>
      config && period && rows
        ? computeScorecard({
            config,
            rows,
            ytdRows,
            plan,
            year: period.year,
            month: period.month,
          })
        : null,
    [config, rows, ytdRows, plan, period]
  );

  if (!config || !period) return null;

  const title = translateLabels(labels.REVIEW_SCORECARD_TITLE, {
    default: "Scorecard",
  });

  return (
    <Paper
      elevation={0}
      data-testid={"review-scorecard"}
      sx={{
        mb: 2,
        backgroundColor: "grey.50",
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        py: 1,
        px: 2,
      }}
    >
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        gap={2}
      >
        <Stack direction={"row"} alignItems={"center"} gap={1}>
          <IconButton
            size={"small"}
            onClick={() => setExpanded(!expanded)}
            aria-label={expanded ? "collapse scorecard" : "expand scorecard"}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Typography variant={"subtitle2"}>{title}</Typography>
          {isLoading && <DefaultLoader size={14} />}
        </Stack>
        <Stack direction={"row"} alignItems={"center"} gap={0.5}>
          <IconButton
            size={"small"}
            onClick={() => setPeriod((p) => shiftPeriod(p, -1))}
            aria-label={"previous month"}
          >
            <ChevronLeftIcon fontSize={"small"} />
          </IconButton>
          <Typography
            variant={"body2"}
            sx={{ minWidth: 140, textAlign: "center", fontWeight: 500 }}
            data-testid={"review-scorecard-period"}
          >
            {periodLabel(period)}
          </Typography>
          <IconButton
            size={"small"}
            onClick={() => setPeriod((p) => shiftPeriod(p, 1))}
            aria-label={"next month"}
          >
            <ChevronRightIcon fontSize={"small"} />
          </IconButton>
        </Stack>
      </Stack>
      <Collapse in={expanded}>
        {error && (
          <Typography variant={"body2"} color={"error"} sx={{ py: 1 }}>
            {error}
          </Typography>
        )}
        {table && (
          <Box sx={{ overflowX: "auto", pt: 1 }}>
            <Table size={"small"} aria-label={title}>
              <TableHead>
                <TableRow>
                  <TableCell />
                  {table.columns.map((column) => (
                    <TableCell key={column.key} align={"right"}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {table.rows.map((row) => (
                  <TableRow
                    key={row.key}
                    sx={rowSx(row.kind)}
                    data-testid={`review-scorecard-row-${row.key}`}
                  >
                    <TableCell component={"th"} scope={"row"}>
                      {row.kind === SCORECARD_ROW_KIND.YTD
                        ? `${row.label} (${period.year})`
                        : row.label}
                    </TableCell>
                    {table.columns.map((column) => {
                      const value = row.values[column.key];
                      return (
                        <TableCell
                          key={column.key}
                          align={"right"}
                          sx={negativeSx(value)}
                        >
                          {formatScorecardValue(column, value)}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
      </Collapse>
    </Paper>
  );
};

export default ReviewScorecard;
